'use strict';

const isZodObject = (schema) => schema?._def
    ?.typeName === 'ZodObject';
const isZodArray = (schema) => schema?._def?.typeName === 'ZodArray';
const isZodUnion = (schema) => schema?._def?.typeName === 'ZodUnion';
const isZodIntersection = (schema) => schema?._def
    ?.typeName === 'ZodIntersection';
const isZodDiscriminatedUnion = (schema) => schema?._def?.typeName === 'ZodDiscriminatedUnion';
const isZodMap = (schema) => schema?._def
    ?.typeName === 'ZodMap';
const isZodSet = (schema) => schema?._def?.typeName === 'ZodSet';
const isZodRecord = (schema) => schema?._def?.typeName === 'ZodRecord';

const ARRAY_ITEM_WILDCARD = '0';
const MAP_WILDCARD = '__key__';
/** Returns all possible paths in the schema and whether they are required (as in not optional and not nullable)
 *
 * @example
 * getSchemaPathRequirementMap(
 *   z.object({
 *     array: z.array(
 *       z.object({
 *         requiredString: z.string().refine(() => true),
 *         optionalString: z.string().trim().max(20).optional(),
 *       }),
 *     ),
 *     requiredBoolean: z.boolean(),
 *   }),
 * )
 *
 * // returns {array: true, array.0: true, array.0.requiredString: true, array.0.optionalString: false, requiredBoolean: true}
 */
const getSchemaPathRequirementMap = (schema) => {
    const pathRequirementMap = {};
    const checkPathInSchema = (schema, path) => {
        const [strippedSchema, strippedLayers] = stripZodWrappers(schema);
        const isCurrentSubpathRequired = !hasOptionalWrapper(strippedLayers);
        /** In zod intersections, unions and discriminated unions the same path can refer to different schemas. E.g in:
         * z.object({
         *   a: z.union([
         *     z.object({ b: z.string() }),
         *     z.object({ c: z.string() }),
         *   ]),
         * });
         * the path "a" can refer to either the z.union schema, or one of the objects schemas inside the union.
         * Determining if a union, discriminated union or intersection is required is handled separately, so we
         * need to avoid overwriting that value here.
         * */
        if (path !== '' && !(path in pathRequirementMap)) {
            pathRequirementMap[path] = isCurrentSubpathRequired;
        }
        if (isZodObject(strippedSchema)) {
            const nestedPaths = Object.keys(strippedSchema.shape);
            for (const nestedPath of nestedPaths) {
                checkPathInSchema(strippedSchema.shape[nestedPath], joinPaths(path, nestedPath));
            }
        }
        if (isZodArray(strippedSchema)) {
            // array elements are represented via the "0" index for easier lookup
            checkPathInSchema(strippedSchema.element, joinPaths(path, ARRAY_ITEM_WILDCARD));
        }
        if (isZodSet(strippedSchema)) {
            // path elements are represented via the "0" index for easier lookup
            checkPathInSchema(strippedSchema._def.valueType, joinPaths(path, ARRAY_ITEM_WILDCARD));
        }
        if (isZodUnion(strippedSchema)) {
            let isUnionRequired = isCurrentSubpathRequired;
            for (const option of strippedSchema.options) {
                /** If any part of the union is optional, the whole union acts as optional.
                 *
                 * z.union([z.string(), z.number()]).optional().safeParse(undefined)  // returns true
                 * z.union([z.string().optional(), z.number()]).safeParse(undefined)  // returns true
                 * z.union([z.string(), z.number()]).safeParse(undefined)             // returns false
                 *
                 */
                const isUnionPartRequired = checkPathInSchema(option, path);
                if (!isUnionPartRequired) {
                    isUnionRequired = false;
                }
            }
            pathRequirementMap[path] = isUnionRequired;
            return isUnionRequired;
        }
        if (isZodDiscriminatedUnion(strippedSchema)) {
            for (const option of strippedSchema.options) {
                checkPathInSchema(option, path);
            }
            // discriminated unions are only optional if the discriminated unions schema itself is optional, so
            // we don't need to check the schemas inside it
        }
        if (isZodIntersection(strippedSchema)) {
            const isLeftPartRequired = checkPathInSchema(strippedSchema._def.left, path);
            const isRightPartRequired = checkPathInSchema(strippedSchema._def.right, path);
            /** A zod intersection acts as optional if either the intersection schema itself is optional, or all its parts are optional.
             *
             * z.intersection(z.enum(['hello']), z.string()).optional().safeParse(undefined)            // returns true
             * z.intersection(z.enum(['hello']).optional(), z.string().optional()).safeParse(undefined) // returns true
             * z.intersection(z.enum(['hello']), z.string().optional()).safeParse(undefined)            // returns false
             */
            const isIntersectionOptional = !isCurrentSubpathRequired ||
                (!isRightPartRequired && !isLeftPartRequired);
            pathRequirementMap[path] = !isIntersectionOptional;
            return !isIntersectionOptional;
        }
        if (isZodMap(strippedSchema) || isZodRecord(strippedSchema)) {
            // since any string is valid as a map key, it is represented via a wildcard for easier lookup
            checkPathInSchema(strippedSchema._def.valueType, joinPaths(path, MAP_WILDCARD));
        }
        return isCurrentSubpathRequired;
    };
    checkPathInSchema(schema, '');
    return pathRequirementMap;
};
const hasZodWrappers = (schema) => Boolean(schema.sourceType ||
    schema._def
        ?.innerType);
const stripZodWrappers = (schema, _strippedTypes = []) => {
    const strippedTypes = [..._strippedTypes];
    let strippedSchema = schema;
    if (hasZodWrappers(schema)) {
        strippedTypes.push(schema._def?.typeName);
        strippedSchema =
            schema.sourceType?.() ??
                schema._def
                    ?.innerType ??
                schema;
    }
    if (hasZodWrappers(strippedSchema)) {
        return stripZodWrappers(strippedSchema, strippedTypes);
    }
    return [strippedSchema, strippedTypes];
};
const hasOptionalWrapper = (wrappers) => wrappers.includes('ZodOptional') || wrappers.includes('ZodNullable');
const joinPaths = (rootPath, path) => [rootPath, path].filter((path) => path !== '').join('.');

exports.ARRAY_ITEM_WILDCARD = ARRAY_ITEM_WILDCARD;
exports.MAP_WILDCARD = MAP_WILDCARD;
exports.getSchemaPathRequirementMap = getSchemaPathRequirementMap;
exports.joinPaths = joinPaths;
//# sourceMappingURL=getSchemaPathRequirementMap-DIM0tidl.js.map
